import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { IActiveHospital, IActiveHospitalProvider, IContext } from "./types";
import {
  getActiveHospitalStorage,
  getLastHospitalSelected,
  setActiveHospitalStorage,
} from "./utils";
import { useAuth } from "@features/auth";
import newTokenService from "@features/auth/services/newTokenService";
import api from "@services/newApi/api";
import { hasHospitalScope, ROLE_CONVENIO, ROLE_DOCTOR, ROLE_HOSPITALS, ROLE_PATIENT } from "@/types/role";
import { FORBIDDEN_AT_HOSPITAL, type ForbiddenAtHospital } from "@contracts";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@lib/queryKeys";
import { getHospitals } from "./ChangeActiveHospitalModal/utils";

export const ActiveHospitalContext = createContext<IContext>({} as IContext);

export const ActiveHospitalProvider = ({
  children,
}: IActiveHospitalProvider) => {
  const auth = useAuth();
  const queryClient = useQueryClient();

  const getLastHospitalSelectedRequestRef = useRef<AbortController | null>(
    null,
  );

  const [activeHospital, setActiveHospital] = useState<
    IActiveHospital | undefined
  >();
  const [changeActiveHospitalIsOpen, setChangeActiveHospitalIsOpen] =
    useState<boolean>(false);
  // `activeHospital` existe assim que o storage é lido, mas o TOKEN só ganha o
  // claim quando o POST /hospitals/active volta. Gatear tela por
  // `activeHospital?.id` dispara as queries antes disso e o backend, que agora
  // exige o hospital do claim, responde 403 em todo reload.
  const [scopeReady, setScopeReady] = useState<boolean>(false);

  // Re-minta o token escopado ao hospital e revalida (repovoa user.permissoes
  // a partir do token). O access token perde o activeHospitalId no refresh e no
  // reload (só vive em memória); re-mintar no mount mantém o escopo — necessário
  // para o role 2 sob AUTHZ_ENFORCE, senão os grants HOSPITAL caem.
  // Caminho ÚNICO de escopo: o modal de troca também passa por aqui, senão
  // `scopeReady` mentiria depois de trocar de hospital.
  const activateHospital = useCallback(async (hospitalId: number) => {
    setScopeReady(false);
    await api.post("/hospitals/active", { hospitalId });
    const validation = await api.post("/auth/validation");
    if (validation.data?.user) {
      auth.setUser(validation.data.user);
    }
    setScopeReady(true);
  }, []);

  const remintScopedToken = useCallback(
    async (hospitalId: number) => {
      try {
        await activateHospital(hospitalId);
      } catch (error) {
        // 403 aqui = o hospital do storage não é (mais) deste usuário. Engolir
        // e manter a seleção deixa toda query pedindo dado de um escopo que o
        // token não tem → 403 opaco em cada tela. Limpa e faz escolher de novo.
        console.error(
          "Falha ao re-escopar hospital ativo:",
          (error as any)?.message,
        );
        setActiveHospital(undefined);
        setChangeActiveHospitalIsOpen(true);
      }
    },
    [activateHospital],
  );

  const getLastHospitalSelectedCallback = useCallback(async () => {
    try {
      const lastHospitalSelected = await getLastHospitalSelected();
      if (lastHospitalSelected?.uuid) {
        setActiveHospital({
          id: parseInt(lastHospitalSelected.id),
          name: lastHospitalSelected.name,
          permissions: lastHospitalSelected.permissions,
          uuid: lastHospitalSelected.uuid,
          isProcedenciaActive: lastHospitalSelected.isProcedenciaActive,
        });
        if (auth.user?.role_id === ROLE_HOSPITALS && lastHospitalSelected.id) {
          await remintScopedToken(Number(lastHospitalSelected.id));
        }
        return;
      }
    } catch (error) {
      console.error("Falha ao recuperar último hospital ativo:", error);
    }

    if (auth.user?.status !== 5 && auth.user?.role_id !== 5) {
      setChangeActiveHospitalIsOpen(true);
    }
    // `auth.user?.role_id` e `status` entram nas deps porque são LIDOS aqui dentro. Sem eles o
    // callback guardava a identidade de quando foi criado: ao personificar um usuário hospitalar a
    // partir de um admin, a checagem `role_id === ROLE_HOSPITALS` avaliava o papel do ADMIN, o
    // remint não acontecia e `scopeReady` ficava falso para sempre — a lista de usuários esperava
    // no esqueleto indefinidamente.
  }, [remintScopedToken, auth.user?.role_id, auth.user?.status]);

  // O `h` do storage é só uma dica: pode ser de outra pessoa que usou este navegador antes.
  // Confere contra a lista de hospitais DESTA pessoa — a mesma que o seletor busca (mesma
  // queryKey, então o cache é compartilhado e não há endpoint novo). Se a lista não vier,
  // mantém a dica: o backend segue sendo a autoridade (role 2 re-escopa e leva 403).
  const isStoredHospitalAllowed = useCallback(
    async (stored: IActiveHospital, userId: number): Promise<boolean> => {
      try {
        const list = await queryClient.ensureQueryData({
          queryKey: queryKeys.hospitals.activeList(userId),
          queryFn: ({ signal }) => getHospitals(signal),
          staleTime: 6 * 60 * 60 * 1000,
        });
        return list.some((h) =>
          stored.uuid ? h.uuid === stored.uuid : Number(h.id) === stored.id,
        );
      } catch (error) {
        console.error(
          "Falha ao conferir hospital guardado:",
          error instanceof Error ? error.message : error,
        );
        return true;
      }
    },
    [queryClient],
  );

  useEffect(() => {
    let cancelled = false;
    // Só o papel 2 depende do claim de hospital; para os demais o escopo já
    // está pronto (papel 1 bypassa, convênio opera por cd_convenio).
    setScopeReady(auth.user?.role_id !== ROLE_HOSPITALS);

    // Convênio opera por `cd_convenio`, não por hospital — e por isso caía fora de todos os
    // ramos abaixo, sem NUNCA escrever em `activeHospital`. O provider não remonta na troca de
    // identidade (mesma AuthenticatedLayout), então o hospital da identidade anterior continuava
    // em memória: personificando um convênio a partir de um admin, o grid de resultados
    // consultava `/attendances/range` com o uuid do hospital ativo do ADMIN. Limpar aqui fecha o
    // vazamento de escopo entre identidades.
    if (auth.user?.role_id === ROLE_CONVENIO) {
      setActiveHospital(undefined);
      return;
    }

    // Mesmo portão do backend (`isAdminOrHospital`), positivo. A lista de exclusão daqui incluía
    // o médico interno (papel 5), que pedia `/users/hospital-activity` para receber 403 a cada
    // login — medido em 2026-09-10 com o portal rodando. E era verdadeira para `role_id` nulo,
    // que é o estado do primeiro render.
    if (hasHospitalScope(auth.user?.role_id)) {
      // Personificação: o sessionStorage guarda o hospital do ADMIN (mesma aba
      // sobrevive ao reload). Herdá-lo remint-aria para um hospital que o alvo
      // não tem → 403. Deriva do próprio alvo via /users/hospital-activity.
      if (newTokenService.getImpersonatorId() !== null) {
        getLastHospitalSelectedCallback();
        return;
      }

      const activeHospitalFromStorage = getActiveHospitalStorage();

      if (activeHospitalFromStorage) {
        void isStoredHospitalAllowed(
          activeHospitalFromStorage,
          Number(auth.user?.id),
        ).then((allowed) => {
          if (cancelled) return;
          if (!allowed) {
            // Hospital que não é desta pessoa: descarta e cai no "último hospital".
            setActiveHospitalStorage(undefined);
            getLastHospitalSelectedCallback();
            return;
          }
          setActiveHospital(activeHospitalFromStorage);
          // Reload: o token renovado pode ter perdido o escopo — re-minta.
          if (
            auth.user?.role_id === ROLE_HOSPITALS &&
            activeHospitalFromStorage.id
          ) {
            remintScopedToken(activeHospitalFromStorage.id);
          }
        });
      } else {
        getLastHospitalSelectedCallback();
      }
    }

    if (
      auth.user?.role_id === ROLE_DOCTOR ||
      auth.user?.role_id === ROLE_PATIENT
    ) {
      setActiveHospital({ uuid: auth.user!.hash! });
    }

    return () => {
      cancelled = true;
    };
  }, [auth.user?.role_id, auth.user?.hash]);

  // 403 de ESCOPO (a pessoa tem a permissão, só não no hospital ativo): o
  // backend manda em quais hospitais ela TEM. Em vez de negar em silêncio,
  // avisa e abre o seletor — a troca passa pelo fluxo real (activateHospital),
  // e o gate de `scopeReady` remonta a tela, que refaz a busca uma vez.
  useEffect(() => {
    const aoNegarPorHospital = (event: Event) => {
      const detail = (event as CustomEvent<ForbiddenAtHospital>).detail;
      const nomes = (detail?.hospitalsWithAccess ?? []).map((h) => h.name);
      if (nomes.length === 0) return;

      toast.info(
        `Você não tem essa permissão no hospital ativo. Você tem acesso em: ${nomes.join(", ")}.`,
        { toastId: FORBIDDEN_AT_HOSPITAL },
      );
      setChangeActiveHospitalIsOpen(true);
    };

    window.addEventListener(FORBIDDEN_AT_HOSPITAL, aoNegarPorHospital);
    return () =>
      window.removeEventListener(FORBIDDEN_AT_HOSPITAL, aoNegarPorHospital);
  }, []);

  useEffect(() => {
    // Durante personificação não persistir: sobrescreveria o hospital do admin
    // no storage e o vazaria no switch-back.
    if (newTokenService.getImpersonatorId() !== null) return;
    setActiveHospitalStorage(activeHospital);
  }, [activeHospital]);

  useEffect(() => {
    getLastHospitalSelectedRequestRef.current?.abort();
  }, [activeHospital, changeActiveHospitalIsOpen]);

  return (
    <ActiveHospitalContext.Provider
      value={{
        ...activeHospital,
        setChangeActiveHospitalIsOpen,
        changeActiveHospitalIsOpen,
        setActiveHospital,
        scopeReady,
        activateHospital,
      }}
    >
      {children}
    </ActiveHospitalContext.Provider>
  );
};
